import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import AIAgentCTA from "@/components/AIAgentCTA";

const NAV = [
  { to: "/what-we-do", label: "What We Do" },
  { to: "/growth-system", label: "The Growth System" },
  { to: "/growth-os", label: "Growth OS" },
  { to: "/pricing", label: "Pricing" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // Mount-only listener; setScrolled is a stable React setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo-mark.png"
            alt="Toulios Advisory Group"
            data-testid="logo-mark"
            className="w-9 h-9 md:w-10 md:h-10 object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <span className="font-display text-base md:text-lg tracking-tight leading-none">
            Toulios <span className="text-zinc-400">Advisory Group</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm tracking-tight transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            className="text-sm text-zinc-400 hover:text-white transition-colors tracking-tight"
            data-testid="header-cta-book"
          >
            Book a Strategy Call
          </Link>
          <AIAgentCTA testId="header-cta-ai" label="Talk to AI Strategist" />
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-white/[0.06] bg-black/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="container-x py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-white" : "text-zinc-400"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <AIAgentCTA
              testId="mobile-cta-ai"
              label="Talk to AI Strategist"
              className="w-full justify-center"
            />
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="btn-outline-white w-full justify-center"
              data-testid="mobile-cta-book"
            >
              Book a Strategy Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
